<!-- Copyright 2022, Common Good Learning Tools LLC -->
<template><div>
	<k-dialog v-model="dialog_open" persistent scrollable>
		<k-card>
			<k-card-title style="border-bottom:1px solid #999">
				<b>Resource Completion Report:</b>
				&nbsp;
				<span v-html="collection.title"></span>
				<k-spacer/>
				<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-on="on" fab x-small class="mr-3" color="secondary" accessible-label="Refresh resource completion report" @click="get_todo_report_data"><k-icon small>fas fa-sync-alt</k-icon></k-btn></template>Refresh report data</k-tooltip>
				<k-btn small color="secondary" @click="download_report"><k-icon small class="mr-1">fas fa-download</k-icon> Download Report</k-btn>
			</k-card-title>
			<k-card-text class="mt-3" style="font-size:16px">

				<div class="py-4 d-flex align-center">
					<k-select outlined dense background-color="#fff" hide-details class="" label="Show report for division/school…" placeholder="" :items="groups" v-model="todo_report_group_showing"></k-select>
					<k-spacer/>
					<k-checkbox class="mt-0 pt-0 mx-3" :label="'Show users with no completions'" v-model="todo_report_show_empty_users" hide-details></k-checkbox>
					<k-spacer/>
					<k-text-field
						v-model="search"
						prepend-inner-icon="fa fa-search" clearable clear-icon="fa fa-times-circle"
						prepend-decorative
						aria-label="Search resource completion report"
						label="Search" single-line hide-details outlined dense background-color="#fff" style="flex:0 1 450px"
					></k-text-field>
				</div>

				<k-data-table v-if="todo_report_group_showing" light dense
					:headers="headers"
					:items="rows"
					:search="search"
					:footer-props="footer_options"
					:items-per-page="100"
					class="k-todo-report-table"
				>
					<template v-slot:item="{ item }"><tr>
						<td style="line-height:16px"><nobr><k-btn icon x-small class="mr-1" accessible-label="Show user completion report" @click.stop="show_user_report(item.raw_data.user_id)"><k-icon>fas fa-arrow-circle-right</k-icon></k-btn><span v-html="item.user_name"></span></nobr><br><nobr style="padding-left:24px"><a :href="`mailto:${item.email}`">{{item.email}}</a></nobr></td>
						<td style="font-size:12px; line-height:15px"><span v-html="item.todo_user_group_display"></span></td>
						<td class="text-center"><b>{{item.complete_pct}}</b> <nobr>{{item.last_completion_date}}</nobr></td>
						<td v-for="header in headers.slice(3)" :key="header.unit_id" class="text-center">{{item.unit_complete_pct[header.unit_id]}}</td>
					</tr></template>
				</k-data-table>

			</k-card-text>
			<k-card-actions class="pa-3" style="border-top:1px solid #999">
				<k-spacer/>
				<k-btn color="primary" @click="$emit('dialog_cancel')"><k-icon small class="mr-2">fas fa-times</k-icon> Close Report</k-btn>
			</k-card-actions>
		</k-card>
	</k-dialog>
	<TodoReportUser v-if="user_report_showing" :collection="collection" :user_id="user_report_showing" @dialog_cancel="hide_user_report" />
</div></template>

<script>
import { mapState, mapGetters } from 'vuex'
import TodoReportUser from './../reports/TodoReportUser'

export default {
	components: { TodoReportUser, },
	props: {
		collection: { type: Object, required: true },
	},
	data() { return {
		dialog_open: true,
		footer_options: {
			itemsPerPageOptions: [10,50,100,-1],
		},
		search: '',
		resource_ids: [],
		user_report_showing: false,
	}},
	computed: {
		...mapState([]),
		...mapGetters([]),
		todo_report_show_empty_users: {
			get() { return this.$store.state.lst.todo_report_show_empty_users },
			set(val) { this.$store.commit('lst_set', ['todo_report_show_empty_users', val]) }
		},
		todo_report_group_showing: {
			get() { return this.$store.state.todo_report_group_showing },
			set(val) {
				this.$store.commit('set', ['todo_report_group_showing', val])
			}
		},
		todo_report_department_showing() {
			if (this.todo_report_group_showing == 'all') return 'all'
			let o = this.groups.find(x=>x.value == this.todo_report_group_showing)
			if (!o) return '???'	// shouldn't happen
			return o.text
		},
		// remember that this, and many other things here, are repeated in TodoReporCollectionUnit
		groups() {
			let arr = []

			let divisions_added = 0
			let schools_added = 0

			// add divisions
			for (let uuid in this.$store.state.todo_user_group_divisions) {
				// skip divisions the user can't see
				if (!vapp.has_admin_right('pd_rep.' + uuid)) continue

				++divisions_added
				arr.push({value: uuid, text: this.$store.state.todo_user_group_divisions[uuid]})
			}

			// add schools
			for (let uuid in this.$store.state.todo_user_group_schools) {
				// skip schools the user can't see
				if (!vapp.has_admin_right('pd_rep.' + uuid)) continue

				++schools_added
				arr.push({value: uuid, text: this.$store.state.todo_user_group_schools[uuid]})
			}

			// if the user is allowed to access at least one division AND at least one school, add headers
			if (schools_added > 0 && divisions_added > 0) {
				// insert schools header after the last division
				arr.splice(divisions_added, 0, {header: '--- SCHOOLS ---'})

				// insert divisions header at the start
				arr.unshift({header: '--- DIVISIONS ---'})
			}

			// add 'all' at the start of the list if appropriate
			if (vapp.has_admin_right('pd_rep.all')) {
				arr.unshift({value: 'all', text: 'All divisions and schools'})
			}

			// if the menu only has one item, select it after the component has time to load
			if (arr.length == 1) {
				setTimeout(x=>{
					this.todo_report_group_showing = arr[0].value
				}, 100)
			}

			return arr
		},
		headers() {
			let arr = []

			// for each unit
			for (let unit of this.collection.units) {
				// count number of complete resources for this unit
				let unit_n_resources = 0
				for (let resource of unit.resources) {
					// skip resources that aren't marked todo
					if (!resource.todo) continue
					++unit_n_resources
				}

				// abbreviated title + number of resources
				let title = unit.title.substr(0, 12)
				if (title != unit.title) title += '…'
				title = sr('$1 ($2)', title, unit_n_resources)
				arr.push({
					text: title,
					align: 'center',
					sortable: true,
					unit_id: unit.lp_unit_id,
					value: 'cp_' + unit.lp_unit_id,
				})
			}

			// add user, division/school, and % columns on top
			arr.unshift({ text: '% Complete', align: 'center', sortable: true, value:'n_complete' })
			arr.unshift({ text: 'Division or School', align: 'left', sortable: true, value:'todo_user_group_display' })
			arr.unshift({ text: 'User', align: 'left', sortable: true, value:'user_name' })

			return arr
		},
		todo_report_group_data() {
			// if the user hasn't yet chosen a group, or if that group's data hasn't been loaded, return an empty array; otherwise return the group's data array
			if (empty(this.todo_report_group_showing)) return []
			let arr = this.$store.state.todo_report_group_data[this.todo_report_group_showing]
			if (empty(arr)) return []
			else return arr
		},
		rows() {
			console.warn('todo_report_show_empty_users', this.todo_report_show_empty_users)
			let arr = []

			for (let user of this.todo_report_group_data) {
				// skip users with 'xxx' in the email
				if (user.email.includes('xxx')) continue

				// if we have a district_department value for this user, don't show the user unless the selected group is one of the district_department values
				if (this.todo_report_department_showing != 'all') {
					if (user.district_department && user.district_department.length > 0) {
						// have to do some corrections for data inconsistencies
						if (!this.correct_school_errors(user.district_department).includes(this.todo_report_department_showing)) {
							continue
						}
					}
				}

				// get todo_user_group_display: first try using district_department values; use todo_user_group value as a backup
				let todo_user_group_display = '<b style="color:#999">Not chosen</b>'
				if (user.district_department && user.district_department.length > 0) {
					todo_user_group_display = user.district_department.join(', ')
				} else if (user.todo_user_group) {
					if (this.$store.state.todo_user_group_divisions[user.todo_user_group]) {
						todo_user_group_display = this.$store.state.todo_user_group_divisions[user.todo_user_group] + '*'
					} else if (this.$store.state.todo_user_group_schools[user.todo_user_group]) {
						todo_user_group_display = this.$store.state.todo_user_group_schools[user.todo_user_group] + '*'
					} else {
						todo_user_group_display = '???'
					}
				}

				let n_resources = 0
				let n_complete = 0
				let row = {
					user_name: user.last_name + ', ' + user.first_name,
					email: user.email,	// .replace(/@henry.*/, '@henry)
					last_completion_date: 0,
					raw_data: user,
					unit_complete_pct: {},
					todo_user_group_display: todo_user_group_display,
				}

				// for each unit
				for (let unit of this.collection.units) {
					// count number of complete resources for this unit
					let unit_n_complete = 0
					let unit_n_resources = 0
					for (let resource of unit.resources) {
						// skip resources that aren't marked todo
						if (!resource.todo) continue

						++unit_n_resources
						// note that the todo_status for a video may be 5-95, indicating partial completion
						if (user.todo_status[resource.resource_id] > 100) {
							++unit_n_complete
							if (user.todo_status[resource.resource_id] > row.last_completion_date) row.last_completion_date = user.todo_status[resource.resource_id]
						}
					}

					// in complete_pct, account for case where we actually don't have any resources to show here
					row['cp_' + unit.lp_unit_id] = unit_n_complete
					row.unit_complete_pct[unit.lp_unit_id] = ((unit_n_resources == 0) ? '' : Math.round(unit_n_complete / unit_n_resources * 100) + '%')

					// add unit_n_complete to n_complete and update n_resources
					n_complete += unit_n_complete
					n_resources += unit_n_resources
				}

				// if todo_report_show_empty_users is false, skip users with no competions
				if (!this.todo_report_show_empty_users && n_complete == 0) continue

				// format last_completion_date
				if (row.last_completion_date == 0) row.last_completion_date = ''
				else row.last_completion_date = date.format(new Date(row.last_completion_date*1000), 'M/D/Y')

				// in complete_pct, account for case where we actually don't have any resources to show here
				row.n_complete = n_complete
				row.complete_pct = (n_resources == 0) ? '' : Math.round(n_complete / n_resources * 100) + '%'

				arr.push(row)
			}

			// by default sort by name
			arr.sort((a,b)=>{
				if (a.user_name < b.user_name) return -1
				if (b.user_name < a.user_name) return 1
				return 0
			})

			return arr
		},
	},
	watch: {
		todo_report_group_showing: { immediate: true, handler() {
			if (!empty(this.todo_report_group_showing) && this.todo_report_group_data.length == 0) {
				this.get_todo_report_data()
			}
		}},
	},
	created() {
	},
	mounted() {
	},
	methods: {
		get_todo_report_data() {
			let payload = {
				todo_user_group: this.todo_report_group_showing
			}
			if (this.todo_report_department_showing != 'all') {
				payload.department = this.todo_report_department_showing
			}

			this.$store.dispatch('get_todo_report_data', payload)
		},

		show_user_report(user_id) {
			this.user_report_showing = user_id*1
		},

		hide_user_report() {
			this.user_report_showing = false
		},

		download_report() {
			let filename = sr('Resource Completion Report - $1.csv', this.collection.title)

			let table = [['User', 'Division/School', '% Complete']]
			for (let i = 3; i < this.headers.length; ++i) {
				table[0].push(this.headers[i].text.replace(/…/g, '...'))
			}
			for (let row of this.rows) {
				let user_name = $('<div>' + row.user_name + '</div>').text()
				let group = $('<div>' + row.todo_user_group_display + '</div>').text()
				let arr = [user_name, group, row.complete_pct]
				for (let lp_unit_id in row.unit_complete_pct) {
					arr.push(row.unit_complete_pct[lp_unit_id])
				}
				table.push(arr)
			}

			U.download_file(CSV.stringify(table), filename)
		},

		correct_school_errors(dept_arr) {
			let arr = []
			for (let s of dept_arr) {
				if (s.includes('Carmel')) s = 'Mount Carmel Elementary'
				if (s.includes('Pate')) s = 'Pates Creek Elementary'
				if (s.includes('Timber')) s = 'Timber Ridge Elementary'
				s = s.replace(/^Eagle.* (\w+)$/, 'Eagles Landing $1')
				arr.push(s)
			}
			return arr
		},
	}
}
</script>

<style lang="scss">
</style>
